import React, { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const TABS = [
  { slug: "forecast",  title: "Forecast",         body: "The real €-forecast for the quarter — worst / likely / best, with the reasoning behind every number.",     src: "/screens/forecast.webp",  highlight: { top: "22%", left: "26%", label: "Weighted forecast" } },
  { slug: "risk",      title: "Risk Radar",       body: "Deals that need attention today, sorted by € at risk with a specific reason for each.",                     src: "/screens/risk.webp",      highlight: { top: "12%", left: "62%", label: "At risk today" } },
  { slug: "funnel",    title: "Funnel X-Ray",     body: "Stage conversion, cycle time, and ACV trends on YOUR history — not textbook averages.",                     src: "/screens/funnel.webp",    highlight: { top: "22%", left: "22%", label: "Median days / stage" } },
  { slug: "team",      title: "Team",             body: "Per-rep coverage, win rate, cycle length, and forecast reliability. No vanity averages.",                    src: "/screens/team.webp",      highlight: null },
  { slug: "bookings",  title: "Bookings",         body: "What you actually closed — month over month, quarter to date, with forecast-accuracy tracking.",             src: "/screens/bookings.webp",  highlight: { top: "18%", left: "22%", label: "This quarter" } },
  { slug: "briefings", title: "Monday Briefing",  body: "One-page AI narrative of your week — the number, why it moved, and the 2-3 deals worth calling today.",     src: "/screens/briefings.webp", highlight: { top: "18%", left: "22%", label: "Why it moved" } },
];

export const ProductTabs = () => {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (paused) return;
    timer.current = setTimeout(() => setI((p) => (p + 1) % TABS.length), 3500);
    return () => clearTimeout(timer.current);
  }, [i, paused]);

  const go = (delta) => {
    setPaused(true);
    setI((p) => (p + delta + TABS.length) % TABS.length);
  };
  const goTo = (n) => { setPaused(true); setI(n); };

  const t = TABS[i];

  return (
    <section
      className="bg-slate-950 border-y border-slate-800 relative overflow-hidden"
      data-testid="landing-product-tabs"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 25% 30%, #fff 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
      <div className="absolute top-1/2 left-1/4 h-96 w-96 bg-emerald-500/10 blur-3xl rounded-full pointer-events-none" aria-hidden="true" />

      <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-emerald-400/80">
            <span className="text-emerald-500/70">/</span>the_product
          </div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
            Six live tabs behind the Monday email.
          </h2>
          <p className="mt-3 text-slate-400 leading-relaxed">
            Real screenshots. Real numbers. Open the app any day — or just read the Monday briefing.
          </p>
        </div>

        {/* Tab pills */}
        <div className="flex justify-center gap-2 flex-wrap mb-6" role="tablist">
          {TABS.map((tab, idx) => (
            <button
              key={tab.slug}
              onClick={() => goTo(idx)}
              className={`px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-widest transition-colors ${idx === i ? "bg-emerald-500 text-slate-950" : "bg-slate-800/70 text-slate-400 hover:bg-slate-700"}`}
              data-testid={`landing-product-tab-${tab.slug}`}
              aria-selected={idx === i}
              role="tab"
            >
              /{tab.slug}
            </button>
          ))}
        </div>

        {/* Carousel viewport */}
        <div className="relative rounded-2xl overflow-hidden bg-white ring-1 ring-slate-800 shadow-2xl">
          {/* Image — all 6 slides preloaded and stacked; we cross-fade opacity
              so switching is instant (no remount, no blank frame). */}
          <div className="relative aspect-[16/9] bg-slate-100 overflow-hidden">
            {TABS.map((tab, idx) => (
              <img
                key={tab.slug}
                src={tab.src}
                alt={`${tab.title} tab screenshot`}
                className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-500 ease-in-out ${idx === i ? "opacity-100" : "opacity-0"}`}
                style={{ willChange: "opacity" }}
                loading="eager"
                decoding="async"
                fetchPriority={idx === 0 ? "high" : "low"}
                aria-hidden={idx !== i}
              />
            ))}
          </div>

          {/* Nav arrows */}
          <button
            onClick={() => go(-1)}
            aria-label="Previous"
            className="absolute left-3 top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full bg-slate-900/80 text-white hover:bg-slate-900 flex items-center justify-center backdrop-blur"
            data-testid="landing-product-prev"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={() => go(1)}
            aria-label="Next"
            className="absolute right-3 top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full bg-slate-900/80 text-white hover:bg-slate-900 flex items-center justify-center backdrop-blur"
            data-testid="landing-product-next"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          {/* Caption (sits below the screenshot — never overlaps) */}
          <div className="p-6 bg-white border-t border-slate-200">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[9px] uppercase tracking-widest text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">
                <span className="text-emerald-500/80">/</span>{t.slug}
              </span>
              <h3 className="text-lg font-bold text-slate-900">{t.title}</h3>
            </div>
            <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{t.body}</p>
          </div>
        </div>

        {/* Dots */}
        <div className="mt-6 flex justify-center gap-2">
          {TABS.map((tab, idx) => (
            <button
              key={tab.slug}
              onClick={() => goTo(idx)}
              aria-label={`Go to ${tab.title}`}
              className={`h-1.5 rounded-full transition-all ${idx === i ? "w-8 bg-emerald-500" : "w-2 bg-slate-700 hover:bg-slate-600"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductTabs;
